// src/components/forms/FormCreator.jsx
import React, { useEffect, useState } from 'react';
import {
    Box, Button, Card, Checkbox, Divider, FormControlLabel, MenuItem, Modal,
    Select, TextField, Typography, FormControl, Chip, Popover, InputAdornment,
    Switch, Radio, RadioGroup
} from '@mui/material';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import { ContentState, EditorState, convertToRaw } from 'draft-js';
import { Editor } from 'react-draft-wysiwyg';
import draftToHtml from 'draftjs-to-html';
import htmlToDraft from 'html-to-draftjs';
import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css';
import Services from 'src/services/Services';
import campaignStatusList from 'src/data/campaignStatusList.json';

const toStatusObjects = (raw) => {
    if (!Array.isArray(raw)) return [];
    return raw.map(s => {
        if (typeof s === 'number') {
            const found = campaignStatusList.find(x => x.status === s);
            return found ? found : { status: s, label: `Durum ${s}`, color: '#E5E7EB' };
        }
        if (s && typeof s === 'object' && typeof s.status === 'number') {
            const found = campaignStatusList.find(x => x.status === s.status);
            return {
                status: s.status,
                label: s.label ?? found?.label ?? `Durum ${s.status}`,
                color: s.color ?? found?.color ?? '#E5E7EB',
            };
        }
        return null;
    }).filter(Boolean);
};

function normalizeQuestionFromApi(item) {
    const c = item?.content || {};
    const rawLabels = c.labels;
    const labels = rawLabels && !Array.isArray(rawLabels) ? rawLabels : {};
    return {
        questionID: item?.questionID,
        label: c.label || "",
        type: c.type || "short",
        options: Array.isArray(c.options) ? c.options : [],
        labels,
        _meta: {
            id: item?.id,
            createdDateTime: item?.createdDateTime,
            status: item?.status,
            createdBy: item?.createdBy,
        },
    };
}

function getContrastColor(hex) {
    try {
        const c = hex.replace('#', '');
        const r = parseInt(c.length === 3 ? c[0] + c[0] : c.substring(0, 2), 16);
        const g = parseInt(c.length === 3 ? c[1] + c[1] : c.substring(2, 4), 16);
        const b = parseInt(c.length === 3 ? c[2] + c[2] : c.substring(4, 6), 16);
        const luminance = (0.2126 * r + 0.7152 * g + 0.0722 * b);
        return luminance > 140 ? '#111827' : '#FFFFFF';
    } catch { return '#111827'; }
}

const getFancyChipSx = (bg) => ({
    height: 28,
    borderRadius: '3px',
    px: 0,
    bgcolor: bg,
    padding: '15px',
    color: getContrastColor(bg),
    boxShadow: '0 1px 0 rgba(0,0,0,0.04)',
    '& .MuiChip-label': {
        px: 1.5,
        fontSize: 13,
        fontWeight: 600,
    },
    '& .MuiChip-deleteIcon': {
        margin: 0,
        ml: 1,
        width: 14,
        height: 14,
        borderRadius: '99px',
        backgroundColor: '#F3F3F3',
        color: '#C0C0C0',
        boxShadow: 'inset 0 0 0 1px rgba(0,0,0,0.06)',
        opacity: 1,
        '&:hover': {
            backgroundColor: '#FFFFFF',
            color: '#6B7280',
            opacity: 1,
        },
    },
});

export default function FormCreator({ initialForm, onSave, onCancel }) {
    const [title, setTitle] = useState(initialForm?.title || '');
    const [category, setCategory] = useState(initialForm?.category || '');

    // Soru bankası ve seçimler
    const [questionBank, setQuestionBank] = useState([]);
    const [selectedIds, setSelectedIds] = useState([]);

    // Artık UI'da sadece "sorular"ı yönetiyoruz (plain array)
    const [formElements, setFormElements] = useState(() => {
        if (initialForm?.formElements?.questions) return initialForm.formElements.questions;
        if (Array.isArray(initialForm?.formElements)) return initialForm.formElements;
        return [];
    });

    // Durumlar: yeni formatta öncelik formElements.statuses; yoksa eski kök "statuses"
    const [statuses, setStatuses] = useState(() =>
        toStatusObjects(
            initialForm?.formElements?.statuses ??
            initialForm?.statuses ??
            []
        )
    );

    // Hukuki metinler
    const [showAgreements, setShowAgreements] = useState(false);
    const [agreementsArray, setAgreementsArray] = useState(initialForm?.legalContent || []);
    const [agreementTitle, setAgreementTitle] = useState('');
    const [agreementEditor, setAgreementEditor] = useState(EditorState.createEmpty());
    const [editingAgreementIndex, setEditingAgreementIndex] = useState(null);
    const [previewOpen, setPreviewOpen] = useState(false);

    // initialForm değişirse tekrar normalize et
    useEffect(() => {
        setFormElements(() => {
            if (initialForm?.formElements?.questions) return initialForm.formElements.questions;
            if (Array.isArray(initialForm?.formElements)) return initialForm.formElements;
            return [];
        });
        setStatuses(
            toStatusObjects(
                initialForm?.formElements?.statuses ??
                initialForm?.statuses ??
                []
            )
        );
    }, [initialForm]);

    // Soru bankası
    useEffect(() => {
        (async () => {
            try {
                const parsedUser = JSON.parse(localStorage.getItem('user'));
                const token = parsedUser?.token;
                const resp = await Services.GetCampaignQuestions(token);
                const list = resp?.data?.status && Array.isArray(resp?.data?.result)
                    ? resp.data.result.map(normalizeQuestionFromApi)
                    : [];
                setQuestionBank(list);
            } catch (e) {
                console.error('[FormCreator] GetCampaignQuestions error', e);
                setQuestionBank([]);
            }
        })();
    }, []);

    // Agreements
    const handleAddOrUpdateAgreement = () => {
        const raw = convertToRaw(agreementEditor.getCurrentContent());
        const html = draftToHtml(raw);
        const newAgr = { title: agreementTitle.trim(), content: html };
        if (editingAgreementIndex !== null) {
            setAgreementsArray(arr => arr.map((a, i) => (i === editingAgreementIndex ? newAgr : a)));
        } else {
            setAgreementsArray(arr => [...arr, newAgr]);
        }
        setAgreementTitle('');
        setAgreementEditor(EditorState.createEmpty());
        setEditingAgreementIndex(null);
    };

    const handleEditAgreement = idx => {
        const agr = agreementsArray[idx];
        setAgreementTitle(agr.title);
        const contentBlock = htmlToDraft(agr.content);
        if (contentBlock) {
            const contentState = ContentState.createFromBlockArray(contentBlock.contentBlocks, contentBlock.entityMap);
            setAgreementEditor(EditorState.createWithContent(contentState));
        } else {
            setAgreementEditor(EditorState.createEmpty());
        }
        setEditingAgreementIndex(idx);
    };

    const handleDeleteAgreement = idx => {
        setAgreementsArray(arr => arr.filter((_, i) => i !== idx));
        if (editingAgreementIndex === idx) {
            setAgreementTitle('');
            setAgreementEditor(EditorState.createEmpty());
            setEditingAgreementIndex(null);
        }
    };

    // Soru seçimleri
    const toggleSelect = (questionID) => {
        setSelectedIds(prev =>
            prev.includes(questionID) ? prev.filter(x => x !== questionID) : [...prev, questionID]
        );
    };

    const addSelectedToForm = () => {
        const picked = questionBank.filter(q => selectedIds.includes(q.questionID));
        const next = [...formElements];
        picked.forEach(p => {
            const idx = next.findIndex(x => x.questionID === p.questionID);
            if (idx >= 0) next[idx] = p;
            else next.push(p);
        });
        setFormElements(next);
        setSelectedIds([]); // ekledikten sonra temizle
    };

    const removeFromForm = (questionID) => {
        setFormElements(prev => prev.filter(q => q.questionID !== questionID));
    };

    // Status silme (number status ile)
    const handleDeleteStatus = (status) => {
        setStatuses(prev => prev.filter(s => s.status !== status));
    };

    // ✅ YENİ JSON: formElements artık { questions: [...], statuses: [...] }
    const generateFormJson = () => {
        const id = initialForm?.id || Date.now().toString();
        return {
            formID: initialForm?.formID,
            id,
            title: title.trim(),
            createdAt: new Date().toISOString(),
            category,
            formElements: {
                questions: formElements.map(q => ({
                    questionID: q.questionID,
                    label: q.label,
                    type: q.type,
                    options: ['multiselect', 'single', 'dropdown'].includes(q.type) ? q.options : [],
                    labels: q.type === 'yesno'
                        ? { yesLabel: q.labels?.yesLabel, noLabel: q.labels?.noLabel }
                        : {}
                })),
                statuses: statuses.map(s => s.status)
            },
            // Artık kökte 'statuses' YOK
            legalContent: agreementsArray.map(a => ({ title: a.title, content: a.content }))
        };
    };

    const handleSaveForm = () => {
        const form = generateFormJson();
        console.log('[FormCreator:saveForm]', form);
        onSave?.(form);
    };

    return (
        <Box p={2}>
            <Typography gutterBottom sx={{ color: '#6B7280', fontSize: '14px', fontWeight: '600' }}>
                {initialForm ? 'Formu Düzenle' : 'Yeni Form Oluştur'}
            </Typography>
            <Divider flexItem sx={{ marginTop: '10px' }} />

            <TextField
                fullWidth
                label="Form Adı"
                value={title}
                onChange={e => setTitle(e.target.value)}
                margin="normal"
            />

            <FormControl fullWidth margin="normal">
                <Select
                    value={category}
                    displayEmpty
                    onChange={e => setCategory(e.target.value)}
                >
                    <MenuItem value="" disabled>Form Kategorisi</MenuItem>
                    <MenuItem value="Kategori 1">Kategori 1</MenuItem>
                    <MenuItem value="Kategori 2">Kategori 2</MenuItem>
                    <MenuItem value="Kategori 3">Kategori 3</MenuItem>
                </Select>
            </FormControl>

            {/* Soru Bankası */}
            <Box mt={2} p={2} sx={{ background: '#F7F7F7', borderRadius: 1, border: '1px dashed #ddd' }}>
                <Typography sx={{ fontWeight: 600, mb: 1 }}>Sorular</Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
                    {questionBank.map(q => (
                        <Box key={q.questionID} sx={{ p: 1, border: '1px solid #eee', borderRadius: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={selectedIds.includes(q.questionID)}
                                        onChange={() => toggleSelect(q.questionID)}
                                    />
                                }
                                label={
                                    <Box>
                                        <Typography sx={{ fontWeight: 600 }}>{q.label}</Typography>
                                        <Typography variant="caption" sx={{ color: '#6B7280' }}>{q.type}</Typography>
                                    </Box>
                                }
                            />
                        </Box>
                    ))}
                </Box>
                <Box mt={1} display="flex" justifyContent="flex-end">
                    <Button variant="outlined" onClick={addSelectedToForm} disabled={selectedIds.length === 0}>
                        Seçili Soruları Forma Ekle
                    </Button>
                </Box>
            </Box>

            {/* Formdaki Sorular */}
            <Box mt={3}>
                <Typography sx={{ fontWeight: 600, mb: 1 }}>Formdaki Sorular</Typography>
                {formElements.length === 0 ? (
                    <Typography variant="body2" sx={{ color: '#6B7280' }}>Henüz soru eklenmedi.</Typography>
                ) : (
                    formElements.map(q => (
                        <Box key={q.questionID} sx={{ p: 1, border: '1px solid #eee', borderRadius: 1, mb: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Box>
                                <Typography sx={{ fontWeight: 600 }}>{q.label}</Typography>
                                <Typography variant="caption" sx={{ color: '#6B7280' }}>{q.type}</Typography>
                            </Box>
                            <Button size="small" variant="outlined" onClick={() => removeFromForm(q.questionID)}>
                                Kaldır
                            </Button>
                        </Box>
                    ))
                )}
            </Box>

            {/* Durum Ekleme */}
            <Box mt={3}>
                <Typography sx={{ color: '#6B7280', fontSize: 14, fontWeight: 600, mb: 1 }}>
                    Durum Ekleme
                </Typography>

                <Box display="flex" gap={2} alignItems="center" flexWrap="wrap">
                    {/* Dropdown seçiminde string -> number */}
                    <FormControl size="small" sx={{ width: 260 }}>
                        <Select
                            value=""
                            displayEmpty
                            onChange={(e) => {
                                const val = Number(e.target.value);
                                const selected = campaignStatusList.find(s => s.status === val);
                                if (selected) {
                                    setStatuses(prev => {
                                        if (prev.some(p => p.status === selected.status)) return prev;
                                        return [...prev, selected];
                                    });
                                }
                            }}
                        >
                            <MenuItem value="" disabled>Durum Seçin</MenuItem>
                            {campaignStatusList.map(s => (
                                <MenuItem key={s.status} value={s.status}>{s.label}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Box>

                <Box mt={2} sx={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap', gap: '12px' }}>
                    {statuses.map(s => (
                        <div key={s.status} style={{ border: '1px solid #EBEBEB', padding: '4px', width: 'fit-content', borderRadius: '3px' }}>
                            <Chip
                                label={s.label}
                                onDelete={() => handleDeleteStatus(s.status)}
                                deleteIcon={<CloseRoundedIcon fontSize="small" />}
                                sx={getFancyChipSx(s.color || '#E5E7EB')}
                            />
                        </div>
                    ))}
                </Box>
            </Box>

            {/* Hukuki Metinler */}
            <Button onClick={() => setShowAgreements(true)} sx={{ marginTop: '10px', color: '#DE504A', fontSize: '14px', fontWeight: '600' }}>
                Hukuki Metin Ekle
            </Button>
            <Divider flexItem sx={{ marginTop: '10px' }} />

            {/* Actions */}
            <Box mt={3} display="flex" gap={4} justifyContent="center">
                <Button variant='outlined' sx={{ width: '20%' }} onClick={onCancel}>Vazgeç</Button>
                <Button
                    sx={{ width: '20%' }}
                    variant="outlined"
                    onClick={() => setPreviewOpen(true)}
                    disabled={!title.trim() || formElements.length === 0}
                >
                    Formu Önizle
                </Button>
                <Button
                    sx={{ width: '20%' }}
                    variant="contained"
                    onClick={handleSaveForm}
                    disabled={!title.trim() || formElements.length === 0}
                >
                    Formu Oluştur
                </Button>
            </Box>

            {/* Agreements Modal */}
            <Modal
                open={showAgreements}
                onClose={() => setShowAgreements(false)}
                sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            >
                <Card sx={{ p: 3, width: 800, maxHeight: '80vh', overflowY: 'auto' }}>
                    <Typography variant="h6">Hukuki Metinler</Typography>
                    <TextField
                        fullWidth
                        label="Başlık"
                        value={agreementTitle}
                        onChange={e => setAgreementTitle(e.target.value)}
                        margin="normal"
                    />
                    <div style={{ border: '1px solid #ccc', padding: 8, borderRadius: 4 }}>
                        <Editor
                            editorClassName='editor'
                            editorState={agreementEditor}
                            onEditorStateChange={setAgreementEditor}
                            toolbar={{ options: ['inline', 'blockType', 'fontSize', 'list', 'textAlign', 'link', 'emoji', 'history'] }}
                        />
                    </div>
                    <Box mt={2} display="flex" gap={1} justifyContent="flex-end">
                        <Button onClick={() => setShowAgreements(false)}>Kapat</Button>
                        <Button variant="contained" onClick={handleAddOrUpdateAgreement} disabled={!agreementTitle.trim()}>
                            {editingAgreementIndex !== null ? 'Güncelle' : 'Ekle'}
                        </Button>
                    </Box>

                    {agreementsArray.length > 0 && (
                        <Box mt={3}>
                            {agreementsArray.map((agr, idx) => (
                                <Box key={idx} display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                                    <Typography>• {agr.title}</Typography>
                                    <Box>
                                        <Button size="small" onClick={() => handleEditAgreement(idx)}>Düzenle</Button>
                                        <Button size="small" onClick={() => handleDeleteAgreement(idx)}>Sil</Button>
                                    </Box>
                                </Box>
                            ))}
                        </Box>
                    )}
                </Card>
            </Modal>

            {/* Önizleme */}
            <Modal
                open={previewOpen}
                onClose={() => setPreviewOpen(false)}
                sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            >
                <Card sx={{ p: 3, width: 500, maxHeight: '80vh', overflowY: 'auto' }}>
                    <Typography variant="h6" gutterBottom>
                        Önizleme: {title}
                    </Typography>
                    {formElements.map(q => (
                        <Box key={q.questionID} mb={2}>
                            <Typography>{q.label}</Typography>
                            {q.type === 'short' && <TextField fullWidth disabled />}
                            {q.type === 'long' && <TextField fullWidth multiline rows={3} disabled />}
                            {q.type === 'multiselect' && q.options.map(opt => (
                                <FormControlLabel key={opt} control={<Checkbox disabled />} label={opt} />
                            ))}
                            {q.type === 'single' && (
                                <RadioGroup>
                                    {q.options.map(opt => (
                                        <FormControlLabel key={opt} control={<Radio disabled />} label={opt} />
                                    ))}
                                </RadioGroup>
                            )}
                            {q.type === 'yesno' && (
                                <FormControlLabel
                                    control={<Switch disabled />}
                                    label={`${q.labels?.yesLabel || 'Evet'} / ${q.labels?.noLabel || 'Hayır'}`}
                                />
                            )}
                            {q.type === 'dropdown' && (
                                <FormControl fullWidth>
                                    <Select disabled displayEmpty value="">
                                        <MenuItem value="" disabled>Seçim yapın</MenuItem>
                                        {q.options.map(opt => (
                                            <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            )}
                            {q.type === 'citydistrict' && (
                                <Box display="flex" gap={2}>
                                    <FormControl fullWidth>
                                        <Select disabled displayEmpty value="">
                                            <MenuItem value="" disabled>İl Seçin</MenuItem>
                                        </Select>
                                    </FormControl>
                                    <FormControl fullWidth>
                                        <Select disabled displayEmpty value="">
                                            <MenuItem value="" disabled>İlçe Seçin</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Box>
                            )}
                        </Box>
                    ))}
                    <Button onClick={() => setPreviewOpen(false)}>Kapat</Button>
                </Card>
            </Modal>
        </Box>
    );
}